import { Button, Divider, Form, Input, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    ConsentMail,
    Ds360,
    Ds360V2,
    Ds360V3,
    Ds360V4,
    Ds360V5,
    Ds360V6,
    Ds360V7,
} from './emails/Email';
import {
    useEmailSettingDetail,
    useEmailSettingUpdate,
} from '../hook/useEmailSetting';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ModalDiscardChanges from '../../../components/Shared/modal/discardChanges';
import usePermission from '../../../hooks/usePermission';

const CUSTOMIZEID = {
    DS360V1: '85072b2654a211ef867dfa163e89e3c8',
    DS360V2: '85072b4554a211ef867dfa163e89e3c8',
    DS360V3: '85072b3554a211ef867dfa163e89e3c8',
    DS360V4: '85072b0754a211ef867dfa163e89e3c8',
    DS360V5: '85072b5554a211ef867dfa163e89e3c8',
    DS360V6: '85072ae854a211ef867dfa163e89e3c8',
    DS360V7: '85072b1654a211ef867dfa163e89e3c8',
    CONSENT: '85072af754a211ef867dfa163e89e3c8',
};

const EmailTemplate = ({ id }) => {
    switch (id) {
        case CUSTOMIZEID.DS360V1:
            return <Ds360 />;
        case CUSTOMIZEID.DS360V2:
            return <Ds360V2 />;
        case CUSTOMIZEID.DS360V3:
            return <Ds360V3 />;
        case CUSTOMIZEID.DS360V4:
            return <Ds360V4 />;
        case CUSTOMIZEID.DS360V5:
            return <Ds360V5 />;
        case CUSTOMIZEID.DS360V6:
            return <Ds360V6 />;
        case CUSTOMIZEID.DS360V7:
            return <Ds360V7 />;
        case CUSTOMIZEID.CONSENT:
            return <ConsentMail />;
        default:
            return <Ds360 />;
    }
};

export default function Template({
    form,
    getEmailSettings,
    isEditable,
    setIsEditable,
}) {
    const { t } = useTranslation();

    const { emailDetail, loading } = useEmailSettingDetail();
    const { updateEmailSetting, loading: loadingSave } =
        useEmailSettingUpdate();

    useEffect(() => {
        initForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailDetail?.subjectEmail]);

    const initForm = () => {
        form.setFieldsValue({
            subjectEmail: emailDetail?.subjectEmail,
        });
    };

    const onSave = (values) => {
        updateEmailSetting(
            {
                customizeEmailId: emailDetail?.customizeEmailId,
                subjectEmail: values.subjectEmail,
            },
            () => {
                getEmailSettings();
                setIsEditable(false);
                setChange(false);
            }
        );
    };

    // Discard change
    const [change, setChange] = useState(false);
    const [isOpenDiscard, setIsOpenDiscard] = useState(false);

    const handelOk = async () => {
        setChange(false);
        setIsEditable(false);
        setIsOpenDiscard(false);
        initForm();
    };

    const onCheckIsChange = () => {
        if (change) {
            setIsOpenDiscard(true);
        } else {
            handelOk();
        }
    };

    // Discard change end

    const { isAuditor, isUser } = usePermission();

    return (
        <Spin spinning={loading}>
            <Form
                form={form}
                layout="vertical"
                onFieldsChange={(_, allFields) => {
                    if (allFields.length > 0) {
                        setChange(true);
                    }
                }}
                onFinish={onSave}
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <div className="font-semibold">Last updated :</div>
                        <div>
                            {emailDetail?.modifyBy || '-'},{' '}
                            {emailDetail?.modifyDate}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {!isEditable ? (
                            <Button
                                disabled={isAuditor || isUser}
                                onClick={() => setIsEditable(true)}
                                className="w-32"
                                size="large"
                                type="primary"
                                ghost
                            >
                                {t('Edit')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={onCheckIsChange}
                                    className="w-32"
                                    size="large"
                                    type="primary"
                                    ghost
                                >
                                    {t('Cancel')}
                                </Button>
                                <Button
                                    loading={loadingSave}
                                    className="w-32"
                                    size="large"
                                    type="primary"
                                    htmlType="submit"
                                >
                                    {t('Save')}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className="border border-gray-200 rounded-xl py-6 p-4 mt-6">
                    <Form.Item
                        name="subjectEmail"
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        label="Subject Email"
                    >
                        <Input
                            disabled={!isEditable}
                            size="large"
                            showCount
                            maxLength={70}
                        />
                    </Form.Item>
                    <div>
                        <div>
                            From : {emailDetail?.senderName || '-'}{' '}
                            {`<${emailDetail?.senderEmail || '-'}>`}
                        </div>
                        <div>To : เจ้าของข้อมูล</div>
                    </div>
                    <Divider />
                    <div className="font-semibold">ตัวอย่างเนื้อหาอีเมล</div>
                    <div className="border border-gray-200 p-6 rounded-xl bg-[#FBFBFB] max-w-2xl m-auto font-semibold space-y-1">
                        <EmailTemplate id={emailDetail?.customizeEmailId} />
                    </div>
                </div>
            </Form>
            <ModalDiscardChanges
                open={isOpenDiscard}
                handelOk={handelOk}
                handelClose={() => {
                    setIsOpenDiscard(false);
                }}
            />
        </Spin>
    );
}

// Props type
Template.propTypes = {
    form: PropTypes.object,
    getEmailSettings: PropTypes.func,
    isEditable: PropTypes.bool,
    setIsEditable: PropTypes.func,
};
EmailTemplate.propTypes = {
    id: PropTypes.string,
};
